import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { theme } from '../../../theme/theme'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useModal } from '../../../hooks/useModal'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { Dropdown, Option } from '../../common/Dropdown'
import { Icon, IconChoices } from '../../common/Icon'
import { Button } from '../../common/Button'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { CreateUpdateWidgetModal } from './CreateUpdateWidgetModal'
import { WidgetType } from './widget-type'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { deleteWidget } from '../../../api/society-widgets'
import { ISocietyWidgetModel } from '../../../interfaces/models/society-widgets.interfaces'

interface FunctionSettingsCustomWidgetProps {
  widget: ISocietyWidgetModel
}

export const FunctionSettingsCustomWidget = ({
  widget,
}: FunctionSettingsCustomWidgetProps): React.ReactNode => {
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
  } = useModal()

  const { mutate: mutateDeleteWidget, isPending: isDeletingWidget } =
    useMutation({
      mutationFn: deleteWidget,
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('functionSettingsView.flashMessage.deleteWidgetSuccess')
        )
      },

      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('functionSettingsView.flashMessage.deleteWidgetFailure')
        )
      },
    })
  const onDelete = async (): Promise<void> => {
    mutateDeleteWidget(widget._id)
  }

  const dropdownOptions: Option[] = [
    {
      value: 'edit',
      label: translate('common.actions.edit'),
      onClick: () => openEditModal(),
    },
    {
      value: 'delete',
      label: translate('common.actions.delete'),
      onClick: openDeleteConfirmationModal,
      destructive: true,
    },
  ]

  let widgetType = widget.type

  if (!widgetType) {
    if (widget.content) {
      widgetType = WidgetType.TEXT
    } else if (widget.link) {
      widgetType = WidgetType.LINK
    }
  }

  return (
    <>
      <div
        key={widget._id}
        className="flex w-full items-center rounded bg-neutral-96 px-4 py-2"
      >
        <div className="flex flex-1 items-center">
          <div className="rounded-full bg-white p-2">
            <Icon
              icon={
                widgetType === WidgetType.LINK
                  ? IconChoices.EXTERNAL_LINK
                  : IconChoices.DOCUMENT
              }
            />
          </div>
          <p className="ml-2 mr-4" style={theme.textVariants.baseBold}>
            {widget.title}
          </p>
        </div>
        {dropdownOptions.length > 0 && (
          <Dropdown options={dropdownOptions}>
            <Button
              className="h-10 w-10"
              iconSize={16}
              icon={IconChoices.MORE}
              loading={isDeletingWidget}
              disabled={isDeletingWidget}
              disableTabIndex
            />
          </Dropdown>
        )}
      </div>
      {society && (
        <CreateUpdateWidgetModal
          society={society}
          show={showEditModal}
          close={closeEditModal}
          widget={{ ...widget, type: widgetType }}
        />
      )}
      <ConfirmationModal
        title={translate('functionSettingsView.confirmationModal.deleteTitle')}
        description={translate(
          'functionSettingsView.confirmationModal.deleteDescription'
        )}
        show={showDeleteConfirmationModal}
        close={closeDeleteConfirmationModal}
        onConfirm={onDelete}
        deleteMode
      />
    </>
  )
}
